/**
 * BrokerRowDecision — Post, Link or Rule out one broker row the sweep held (D-281),
 * or match it to a security or a deal (D-284, BrokerRowPlace)
 * ===============================================================================
 * Brian, 27 October 2026: "broker trades should just post, only conversions
 * and other items should be flagged and required for approval", and then
 * "make an actual way for me to approve stuff easily".
 *
 * A reason, then the server's dry run in words, then the button that commits;
 * nothing is written before that button (CLAUDE.md §1). Used on Approvals and
 * in the pop-up after sign-in.
 *
 * "Already booked…" appears where the sweep found the row's cash on the ledger
 * (`item.entry`): posting would book it twice and ruling it out would say it
 * never happened, so it links the row to that entry instead (D-281 §9).
 */
import React, { useState } from "react";
import api from "../api/client";
import BrokerRowPlace, { PLACE_WORDS } from "./BrokerRowPlace";
import { BLOTTER_REVIEW_URL } from "./needsReview";
import "./BrokerRowDecision.css";

const DECISIONS = {
  post: {
    open: "Post…",
    placeholder: "Why it should post, e.g. ITG sold short and covered later",
    commit: "Post it",
    done: "Posted.",
  },
  link: {
    open: "Already booked…",
    placeholder: "Why that entry is this row, e.g. the proceeds were posted from the bank",
    commit: "Link it",
    done: "Linked to the entry that booked it.",
  },
  "rule-out": {
    open: "Rule out…",
    placeholder: "Why it is not a trade to post, e.g. a cancellation pair",
    commit: "Rule it out",
    done: "Ruled out.",
  },
};

/**
 * Post, Link or Rule out one broker row. A reason, then the server's dry run in
 * words, then the button that commits; nothing is written before that button.
 */
export default function BrokerRowDecision({ item, onDone }) {
  const [action, setAction] = useState(null);
  const [why, setWhy] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (commit) => {
    setBusy(true);
    setError(null);
    try {
      const body = action === "link" ? { why, commit, transaction: item.entry } : { why, commit };
      const res = await api.post(BLOTTER_REVIEW_URL(item.row_id, action), body);
      if (commit) onDone(item.key, DECISIONS[action].done);
      else setPreview(res.data.reads_as || "");
    } catch (e) {
      setPreview(null);
      setError(e?.response?.data?.detail || "The server did not answer. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (action === "place") {
    return <BrokerRowPlace item={item} onDone={onDone} onCancel={() => setAction(null)} />;
  }
  if (!action) {
    return (
      <span className="nr-decide">
        {PLACE_WORDS[item.needs] && (
          <button type="button" onClick={() => setAction("place")}>{PLACE_WORDS[item.needs].open}</button>
        )}
        {item.can_post && (
          <button type="button" onClick={() => setAction("post")}>{DECISIONS.post.open}</button>
        )}
        {item.entry && (
          <button type="button" onClick={() => setAction("link")}>{DECISIONS.link.open}</button>
        )}
        <button type="button" onClick={() => setAction("rule-out")}>{DECISIONS["rule-out"].open}</button>
      </span>
    );
  }
  const words = DECISIONS[action];
  return (
    <form className="nr-decision" onSubmit={(e) => { e.preventDefault(); send(false); }}>
      <input
        aria-label={`${words.commit}: why`}
        placeholder={words.placeholder}
        value={why}
        onChange={(e) => { setWhy(e.target.value); setPreview(null); }}
        autoFocus
      />
      <button type="submit" disabled={busy || !why.trim()}>Check</button>
      <button type="button" className="nr-cancel" onClick={() => { setAction(null); setPreview(null); setError(null); }}>
        Cancel
      </button>
      {error && <p className="nr-error" role="alert">{error}</p>}
      {preview !== null && (
        <p className="nr-preview">
          {preview}{" "}
          <button type="button" className="nr-commit" disabled={busy} onClick={() => send(true)}>{words.commit}</button>
        </p>
      )}
    </form>
  );
}

